import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Amplify } from 'aws-amplify';
import {
  autoSignIn,
  signIn,
  signOut,
  updatePassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  resendSignUpCode,
  signUp,
  confirmUserAttribute,
  sendUserAttributeVerificationCode,
  fetchMFAPreference,
  updateMFAPreference,
  fetchUserAttributes,
  rememberDevice,
  SignInOutput,
  SignInInput,
  SignUpOutput,
  updateUserAttribute,
  ConfirmSignInOutput,
  VerifiableUserAttributeKey,
  FetchMFAPreferenceOutput,
  signInWithRedirect,
  UpdateMFAPreferenceInput,
  JWT,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { Hub } from 'aws-amplify/utils';
import { resetStores } from '@datorama/akita';
import { LoginInfo, LoginType } from '@models/profile/model';
import { RaygunService } from 'src/app/raygun/raygun.service';
import { Categories } from 'src/app/shared/constants/analyticsCategory.constant';
import { CommonConstants } from 'src/app/shared/constants/common.constant';
import { CoreService, RememberUserAction, SessionStorage } from 'src/app/shared/models/clux/enum';
import { MatchType } from 'src/app/shared/models/uba/account/model';
import { SearchCriteria } from 'src/app/shared/models/uba/configuration/model';
import { BrowserSessionStorageService } from 'src/app/shared/services/browser-session-storage.service';
import { BrowserService } from 'src/app/shared/services/browser.service';
import { EmitterService } from 'src/app/shared/services/emitter.service';
import { GoogleAnalyticsService } from 'src/app/shared/services/google-analytics.service';
import { PhoneNumbers } from 'src/app/shared/utils/phone-numbers';
import { Uri } from 'src/app/shared/utils/uri';
import { CurrentUserQuery, CurrentUserStore } from 'src/app/state';
import { AmplifyStorage } from './amplify.storage';
import { environment } from 'src/environments/environment';
type AuthMFAType = 'SMS' | 'TOTP' | 'EMAIL';
type StopListenerCallback = () => void;

import { MFAType } from '../models';
import { AuthenticationChallenge, AuthenticationChallengeType } from '../models/authentication-challenge.model';
import { BrandService } from '@app/shared/services/brand.service';
import { SecurityService } from '@app/shared/services/security.service';
import { AuthError } from '../models/auth-error.model';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { DashboardService } from '@app/shared/services/dashboard.service';
import { AuthPreferences } from '@models/dashboard/model';
import { v4 } from 'uuid';

export type AuthStatus = 'LOGGED_OUT' | 'LIMITED_AUTH' | 'FULL_AUTH';

interface ProfileInfo {
  username?: string;
  idToken?: JWT;
  client_count?: number;
  division_count?: number;
  client_id?: string;
}

interface SignInOptions {
  ignoreRedirects?: boolean;
  keepCredentials?: boolean;
}

interface ConfirmSignUpOptions {
  redirectPathOnSuccess?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private browserService = inject(BrowserService);
  private browserSessionStorageService = inject(BrowserSessionStorageService);
  private currentUserQuery = inject(CurrentUserQuery);
  private currentUserStore = inject(CurrentUserStore);
  private emitterService = inject(EmitterService);
  private http = inject(HttpClient);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private raygunService = inject(RaygunService);
  private router = inject(Router);
  private readonly brandService = inject(BrandService);
  private readonly securityService = inject(SecurityService);
  private readonly dashboardService = inject(DashboardService);

  private static readonly SIGNIN_REDIRECT = 'SignInRedirect';
  private static readonly MFA_PREFERENCE_CACHE = 'MfaPreferenceCache';
  private static readonly AUTH_PREFERENCE_CACHE = 'AuthPreferenceCache';

  private _authStorage = window.sessionStorage;

  private _lastConfirmAttribute: string | undefined;
  private _lastDeliveryMedium: string | undefined;
  private _lastDeliveryDestination: string | undefined;

  private _rememberMe: RememberUserAction;

  private _lastCreds: { u: string; p: string; duxToken: string } | undefined;

  private _lastSignInWasComplete = false;

  private hubCallback?: StopListenerCallback;

  private authStatus = new BehaviorSubject<AuthStatus>('LOGGED_OUT');

  public authStatus$: Observable<AuthStatus> = this.authStatus.asObservable();

  public get lastUser(): string {
    return this._lastCreds?.u ?? '';
  }

  public get lastConfirmAttribute(): string | undefined {
    return this._lastConfirmAttribute;
  }

  public get lastDeliveryMedium(): string | undefined {
    return this._lastDeliveryMedium;
  }

  public get lastDeliveryDestination(): string | undefined {
    return this._lastDeliveryDestination;
  }

  public selectedClientSignInTemp: string | undefined;
  public selectedDivisionSignInTemp: string | undefined; // only needed for custom auth flow

  constructor() {
    this.initializeAmplify();
    void this.updateAuthStatus();
  }

  private initializeAmplify(): void {
    const redirectUriBase = this.browserService.getLocationOrigin();
    const cognitoURL = new Uri('', CoreService.Cognito);
    const pathname = this.browserService.getLocationPathname();
    const isIdPi = pathname === '/login/federatedIdPi';

    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: environment.auth.UserPoolId,
          userPoolClientId: isIdPi ? environment.auth.idpiClientId : environment.auth.ClientId,
          loginWith: {
            oauth: {
              domain: `${cognitoURL.getHostAndPort()}`,
              scopes: ['phone', 'email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
              redirectSignIn: [isIdPi ? `${redirectUriBase}/login/federatedIdPi` : `${redirectUriBase}/login/federated`],
              redirectSignOut: [`${redirectUriBase}/logout/federated`],
              responseType: 'code',
            },
          },
        },
      },
    });

    cognitoUserPoolsTokenProvider.setKeyValueStorage(new AmplifyStorage());
  }

  public async signUp(username: string, password: string) {
    let signUpResult: SignUpOutput;

    this.setEmailAddress(username);

    try {
      signUpResult = await signUp({
        username,
        password,
        options: {
          userAttributes: {
            email: username,
          },
          clientMetadata: {
            tpaPartner: this.brandService.getTpaPartner() || 'XXX',
          },
        },
      });
    } catch (error) {
      this.raygunService.logError('Sign up error', { error });

      switch ((error as { name?: string })?.name ?? '') {
        case 'UserLambdaValidationException':
          return 'INVALID_USER_ERROR';
        case 'UsernameExistsException':
          return 'USER_EXISTS_ERROR';
      }

      return 'UNKNOWN_ERROR';
    }

    return this.processSignUpResponse(username, signUpResult, password);
  }

  public async confirmSignUp(username: string, confirmationCode: string, options?: ConfirmSignUpOptions) {
    let confirmSignUpResult: SignUpOutput;

    try {
      confirmSignUpResult = await confirmSignUp({ username, confirmationCode });
    } catch (error) {
      this.raygunService.logError('Confirm sign-up error', { error });

      switch ((error as { name?: string })?.name ?? '') {
        case 'UserLambdaValidationException':
          return 'INVALID_USER_ERROR';
        case 'CodeMismatchException':
          return 'CODE_MISMATCH_ERROR';
      }

      return 'UNKNOWN_ERROR';
    }

    return this.processSignUpResponse(username, confirmSignUpResult, undefined, options);
  }

  private async processSignUpResponse(username: string, signUpOutput: SignUpOutput, password?: string, options?: ConfirmSignUpOptions) {
    if (signUpOutput.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
      this._lastConfirmAttribute = signUpOutput.nextStep.codeDeliveryDetails.attributeName;
      this._lastDeliveryMedium = signUpOutput.nextStep.codeDeliveryDetails.deliveryMedium;
      this._lastDeliveryDestination = signUpOutput.nextStep.codeDeliveryDetails.destination;
      this._lastCreds = { u: username, p: password ?? '', duxToken: undefined };

      await this.router.navigate(['/signup/verify-email']);
    } else if (signUpOutput.nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
      this._lastConfirmAttribute = '';
      this._lastDeliveryMedium = '';
      this._lastDeliveryDestination = '';

      return this.signIn_sub(autoSignIn);
    } else if (signUpOutput.nextStep.signUpStep === 'DONE') {
      if (options?.redirectPathOnSuccess) {
        await this.router.navigateByUrl(options.redirectPathOnSuccess, { state: history.state });
        return '';
      }

      this.setSignInRedirect('signup/userDetails');
      const signInResponse = await this.signInWithStoredCredentials(undefined, false, { keepCredentials: true });

      if (signInResponse === 'NO_SAVED_CREDENTIALS') {
        this._lastConfirmAttribute = '';
        this._lastDeliveryMedium = '';
        this._lastDeliveryDestination = '';
        this._lastCreds = { u: username, p: '', duxToken: undefined };

        await this.router.navigate(['/']);

        const emitterMsg = this.emitterService.getEmitterMessage('Your email has been verified.', true);
        setTimeout(() => this.emitterService.on(CommonConstants.emitterKeys.loginErrors).emit(emitterMsg), 0);

        return '';
      }

      return signInResponse;
    }

    return '';
  }

  public async resendSignUpCode(username: string) {
    try {
      const result = await resendSignUpCode({ username });

      this._lastConfirmAttribute = result.attributeName;
      this._lastDeliveryMedium = result.deliveryMedium;
      this._lastDeliveryDestination = result.destination;

      const emitterMsg = this.emitterService.getEmitterMessage(`Verification code sent to ${username}. This code is valid for 24 hours.`, true);
      this.emitterService.on(CommonConstants.emitterKeys.loginErrors).emit(emitterMsg);
    } catch (error) {
      this.raygunService.logError('Resend sign-up code error', { error });

      switch ((error as { name?: string })?.name ?? '') {
        case 'UserNotFoundException':
          return 'INVALID_USER_ERROR';
        case 'LimitExceededException':
          return 'LIMIT_EXCEEDED_ERROR';
        case 'InvalidParameterException':
          if ((error as { message?: string }).message === 'User is already confirmed.') {
            await this.router.navigate(['/']);
          }
      }

      return 'UNKNOWN_ERROR';
    }

    return '';
  }

  public async signIn(
    username: string,
    password: string,
    _rememberUser: RememberUserAction,
    extra?: { clientId?: string; divisionId?: string },
    options?: SignInOptions
  ) {
    this._lastSignInWasComplete = false;
    this._rememberMe = _rememberUser;
    this.setEmailAddress(username);

    this.selectedClientSignInTemp = extra?.clientId || this.selectedClientSignInTemp;

    const signInInput: SignInInput = {
      username,
      password,
      options: {
        clientMetadata: {
          tpaPartner: this.brandService.getTpaPartner() || 'XXX',
          selectedClientId: extra?.clientId,
          selectedDivisionId: extra?.divisionId,
        },
        authFlowType: 'USER_PASSWORD_AUTH',
      },
    };

    this._lastCreds = { u: username, p: password, duxToken: undefined };

    const error = await this.signIn_sub(async () => signIn(signInInput), options);
    if (error !== 'USER_ALREADY_SIGNED_IN_ERROR') {
      return error;
    }

    await this.signOut(true);
    this._lastCreds = { u: username, p: password, duxToken: undefined };

    return this.signIn_sub(async () => signIn(signInInput));
  }

  public async duxSSOSignIn(username: string, duxToken: string, extra?: { clientId?: string; divisionId?: string }) {
    this._lastSignInWasComplete = false;
    this.setEmailAddress(username);

    this.selectedClientSignInTemp = extra?.clientId || this.selectedClientSignInTemp;
    this.selectedDivisionSignInTemp = extra?.divisionId || this.selectedDivisionSignInTemp;

    const signInInput: SignInInput = {
      username,
      options: {
        authFlowType: 'CUSTOM_WITHOUT_SRP',
        clientMetadata: {
          tpaPartner: this.brandService.getTpaPartner() || 'XXX',
          selectedClientId: extra?.clientId,
          selectedDivisionId: extra?.divisionId,
        },
      },
    };

    this._lastCreds = { u: username, p: undefined, duxToken };
    const error = await this.signIn_sub(async () => signIn(signInInput));

    if (error !== 'USER_ALREADY_SIGNED_IN_ERROR') {
      return error;
    }

    await this.signOut(true);
    this._lastCreds = { u: username, p: undefined, duxToken };

    return this.signIn_sub(async () => signIn(signInInput));
  }

  public async duxSSOSignInAndRedirect(username: string, duxToken: string, extra?: { clientId?: string; divisionId?: string }) {
    const error = await this.duxSSOSignIn(username, duxToken, extra);

    if (!error && this._lastSignInWasComplete) {
      await this.directUserAfterSignIn();
    }

    return error;
  }

  public async signInAndRedirect(username: string, password: string, _rememberUser: RememberUserAction, extra?: { clientId?: string; divisionId?: string }) {
    const error = await this.signIn(username, password, _rememberUser, extra);

    if (!error && this._lastSignInWasComplete) {
      await this.directUserAfterSignIn();
    }

    return error;
  }

  private async signIn_sub(signInFn: () => Promise<SignInOutput>, options?: SignInOptions) {
    let signInResult: SignInOutput;

    try {
      signInResult = await signInFn();
    } catch (error) {
      this.raygunService.logError('Auto sign-in error', { error });

      switch ((error as { name?: string })?.name ?? '') {
        case 'UserLambdaValidationException':
          return 'INVALID_USER_ERROR';
        case 'NotAuthorizedException':
          return 'NOT_AUTHORIZED_ERROR';
        case 'UserAlreadyAuthenticatedException':
          return 'USER_ALREADY_SIGNED_IN_ERROR';
      }

      return 'UNKNOWN_ERROR';
    }

    return this.processSignInResponse(signInResult, options);
  }

  public async confirmSignIn(challengeResponse: string) {
    let signInResult: ConfirmSignInOutput;

    try {
      signInResult = await confirmSignIn({
        challengeResponse,
        options: {
          clientMetadata: {
            tpaPartner: this.brandService.getTpaPartner() || 'XXX',
          },
        },
      });
    } catch (error) {
      this.raygunService.logError('Confirm sign-in error', { error });

      switch ((error as { name?: string })?.name ?? '') {
        case 'UserLambdaValidationException':
          return 'INVALID_USER_ERROR';
        case 'UserAlreadyAuthenticatedException':
          return 'USER_ALREADY_SIGNED_IN_ERROR';
        case 'CodeMismatchException':
          return 'CODE_MISMATCH_ERROR';
        case 'SignInException':
          return 'INVALID_SESSION_ERROR';
      }

      return 'UNKNOWN_ERROR';
    }

    return this.processSignInResponse(signInResult);
  }

  public async confirmSignInAndRedirect(challengeResponse: string) {
    const error = await this.confirmSignIn(challengeResponse);

    if (!error && this._lastSignInWasComplete) {
      await this.directUserAfterSignIn();
    }

    return error;
  }

  public async resendMFACode(mfaType?: AuthMFAType) {
    const signInError = await this.signIn(this._lastCreds?.u ?? '', this._lastCreds?.p ?? '', this._rememberMe, undefined, { ignoreRedirects: true });
    if (signInError) {
      return signInError;
    }

    if (mfaType) {
      const confirmSignInError = await this.confirmSignIn(mfaType);
      if (confirmSignInError) {
        if (confirmSignInError === 'CODE_MISMATCH_ERROR') {
          return '';
        }
        return confirmSignInError;
      }
    }

    return '';
  }

  private async processSignInResponse(signInOutput: SignInOutput, options: SignInOptions = {}) {
    // The sign in process consists of a series of challenges presented by Cognito with the client-side
    // responding to those challenges until a terminal state is reached: an error, auth failure, or auth success.
    // Depending on how Cognito is configured and whether custom auth is utilized, the challenges will
    // vary in their existence and sequence.
    // It is handy to think of this in terms of a finite state machine, represented by the SignInOutput object,
    // which indicates which state transitions to make.
    // Most of the time the challenges require us to present a screen to the user to provide intput,
    // whereby we navigate the user to an appropriate angular route that collects that input and re-enters this
    // response handling branching logic.  In some cases (e.g. DUX SSO) we may already have the necessary input
    // in hand so we don't need to present any screen to the user and can immediately responsd to the challenge
    // where we can then immediately process the new SingnInOutput response.  For this situation, we are wrapping
    // the handler in a do {} while(true) loop that has a forced break at the end.  In this way, most challenges
    // will be responded to by navigating the user to an appropriate angular route and the loop will automatically
    // break and the function will return as expected.  But in the case of scenarios where we already have the
    // response value (e.g. DUX SSO) we can respond to the challenge, overwrite the SignInOutput with the new
    // response from Cognito, and then call continue to loop over the state transition logic again.
    do {
      if (signInOutput.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
        this._lastConfirmAttribute = signInOutput.nextStep.codeDeliveryDetails?.attributeName ?? '';
        this._lastDeliveryDestination = signInOutput.nextStep.codeDeliveryDetails?.destination ?? '';
        this._lastDeliveryMedium = signInOutput.nextStep.codeDeliveryDetails?.deliveryMedium ?? '';

        if (signInOutput.nextStep.codeDeliveryDetails.destination) {
          this.storePhoneNumber(signInOutput.nextStep.codeDeliveryDetails.destination);
        }

        await this.router.navigate(['/for2FA/verify-2FA-mobile']);
      } else if (signInOutput.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
        this._lastConfirmAttribute = signInOutput.nextStep.codeDeliveryDetails?.attributeName ?? '';
        this._lastDeliveryDestination = signInOutput.nextStep.codeDeliveryDetails?.destination ?? '';
        this._lastDeliveryMedium = signInOutput.nextStep.codeDeliveryDetails?.deliveryMedium ?? '';

        if (!options.ignoreRedirects) {
          await this.router.navigate(['/for2FA/verify-2FA-email']);
        }
      } else if (signInOutput.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        this.raygunService.logError(`Received Cognito sign-in response that is not implemented in Clux: ${signInOutput.nextStep.signInStep}`, { signInOutput });
        return 'NOT_IMPLEMENTED_ERROR';
      } else if (signInOutput.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // this response given if a temporary password has been set by an admin...which we aren't using
        this.raygunService.logError(`Received Cognito sign-in response that is not implemented in Clux: ${signInOutput.nextStep.signInStep}`, { signInOutput });
        return 'NOT_IMPLEMENTED_ERROR';
      } else if (signInOutput.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        const challengeName = signInOutput.nextStep.additionalInfo.challenge;

        if (challengeName === 'DUX_TOKEN') {
          try {
            signInOutput = await confirmSignIn({
              challengeResponse: this._lastCreds.duxToken,
              options: {
                clientMetadata: {
                  tpaPartner: this.brandService.getTpaPartner() || 'XXX',
                  selectedClientId: this.selectedClientSignInTemp,
                  selectedDivisionId: this.selectedDivisionSignInTemp,
                },
              },
            });

            // for the DUX_TOKEN custom challenge, we already have the token to respond with
            // so we can respond to the challenge immediately and replace the signInOutput with
            // the new response and call continue here to loop again and process the new signInOutput
            continue;
          } catch (error) {
            this.raygunService.logError(`Custom Challenge confirm sign-in error: ${challengeName}`, { signInOutput, error });
            return 'UNEXPECTED_SIGNIN_RESPONSE';
          }
        } else {
          this.raygunService.logError(`Received Cognito custom challenge that is not implemented in Clux: ${challengeName}`, { signInOutput });
          return 'NOT_IMPLEMENTED_ERROR';
        }
      } else if (signInOutput.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_PASSWORD') {
        // we send username and password together so shouldn't see this response
        this.raygunService.logError(`Received Cognito sign-in response that is not implemented in Clux: ${signInOutput.nextStep.signInStep}`, { signInOutput });
        return 'NOT_IMPLEMENTED_ERROR';
      } else if (signInOutput.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        // if the user abandoned the sign up confirmation process then this response is given at next sign in
        await this.resendSignUpCode(this.currentUserQuery.getEmailAddress());
        await this.router.navigate(['/signup/verify-email']);
      } else if (signInOutput.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
        // when multiple MFA options are configured for the user signing in and there is no preference set
        // then this response is given to ask the user to select one of the MFA options to be challenged with
        if (!options.ignoreRedirects) {
          await this.router.navigate(['/for2FA/mfa-selection'], {
            state: { mfaOptions: signInOutput.nextStep.allowedMFATypes || [] },
          });
        }
      } else if (signInOutput.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION') {
        // when MFA is required on the user pool, and the user doesn't have any MFA set up, and multiple options are available to select
        // then this response is given to require the user to select one to be setup
        this.raygunService.logError(`Received Cognito sign-in response that is not implemented in Clux: ${signInOutput.nextStep.signInStep}`, { signInOutput });
        return 'NOT_IMPLEMENTED_ERROR';
      } else if (signInOutput.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP') {
        // our usernames are email addresses so we shouldn't see this response
        this.raygunService.logError(`Received Cognito sign-in response that is not implemented in Clux: ${signInOutput.nextStep.signInStep}`, { signInOutput });
        return 'NOT_IMPLEMENTED_ERROR';
      } else if (signInOutput.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        // we don't have TOTP requirements on the userpool so we shouldn't see this response
        this.raygunService.logError(`Received Cognito sign-in response that is not implemented in Clux: ${signInOutput.nextStep.signInStep}`, { signInOutput });
        return 'NOT_IMPLEMENTED_ERROR';
      } else if (signInOutput.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
        // we use password as the first factor so shouldn't see this response
        this.raygunService.logError(`Received Cognito sign-in response that is not implemented in Clux: ${signInOutput.nextStep.signInStep}`, { signInOutput });
        return 'NOT_IMPLEMENTED_ERROR';
      } else if (signInOutput.nextStep.signInStep === 'RESET_PASSWORD') {
        // admin initiated forced password reset
        await this.router.navigate(['/two-factor-authentication'], { queryParams: { type: 'reset' } });
      } else if (signInOutput.nextStep.signInStep === 'DONE') {
        this._lastSignInWasComplete = true;
        this._lastConfirmAttribute = '';
        this._lastDeliveryDestination = '';
        this._lastDeliveryMedium = '';

        const profileInfo = await this.getProfileInfo();
        const username = typeof profileInfo.username === 'string' ? profileInfo.username : undefined;
        await this.updateAuthStatus();

        this.setEmailAddress(username);
        await this.setCurrentUser(profileInfo);
        await this.rememberOrForgetDevice();
      } else {
        this.raygunService.logError('Received Unknown Cognito sign-in response.', { signInOutput });
        return 'UNEXPECTED_SIGNIN_RESPONSE';
      }

      // If we reach this point, then the function didn't return an error and we have navigated the user
      // to some angular route to prompt for input to a Cognito challenge.
      // In this case we need to break from the loop since the code that is collecting the user input will
      // re-enter by calling into the auth service in some way after that input is collected.
      break;

      // eslint-disable-next-line no-constant-condition
    } while (true); // loop uses `continue` for DUX_TOKEN re-entry; all other paths break or return

    return '';
  }

  public async directUserAfterSignIn() {
    const profileInfo = await this.getProfileInfo();
    if (profileInfo.client_count > 1) {
      await this.router.navigate(['distributor']);
      return;
    } else if (profileInfo.division_count > 1) {
      await this.router.navigate(['division-select']);
      return;
    }

    this._lastCreds = undefined;

    const isMfaRequired = this.isMfaRequired();
    if (isMfaRequired) {
      await this.router.navigate(['/login/enable-2fa']);
    } else {
      this.googleAnalyticsService.eventEmitter(Categories.EventCategories.SignInSuccess);
      await this.routeToSignInRedirect();
    }
  }

  public async signOut(preserveSignInRedirect: boolean = false): Promise<void> {
    try {
      const redirectUrl = this.getSignInRedirect();

      await signOut({ global: true });
      await this.updateAuthStatus();

      const sessionStorageToClear: string[] = [SessionStorage.Email, CommonConstants.localKeys.showPostManuallyGrowl, CommonConstants.localKeys.showHoldGrowler];
      sessionStorageToClear.map((key) => this.browserSessionStorageService.remove(key));

      this._lastCreds = undefined;
      this._authStorage.clear();
      resetStores();
      this.raygunService.clearUser();

      if (preserveSignInRedirect) {
        this.setSignInRedirect(redirectUrl);
      }
    } catch (error) {
      this.raygunService.logError('Sign out failed', { error });
      throw error;
    }
  }

  public async getValidIdToken(forceRefresh: boolean = false) {
    try {
      let authSession = await fetchAuthSession({ forceRefresh });
      let expiration = authSession.tokens?.idToken?.payload.exp ?? 0;
      const tokenExpired = expiration - 5 <= Date.now() / 1000; // shave 5 seconds so we refresh before a timing issue happens
      if (tokenExpired) {
        authSession = await fetchAuthSession({ forceRefresh: true });
        expiration = authSession.tokens?.idToken?.payload.exp ?? 0;
      }
      return expiration <= Date.now() / 1000 ? null : authSession.tokens?.idToken?.toString();
    } catch (_error) {
      return null;
    }
  }

  public async refreshSession(forceRefresh: boolean = false) {
    const jwt = await this.getValidIdToken(forceRefresh);
    await this.updateAuthStatus();
    return !!jwt;
  }

  public setSignInRedirect(url: string) {
    if (url) {
      this._authStorage.setItem(AuthenticationService.SIGNIN_REDIRECT, url);
    } else {
      this._authStorage.removeItem(AuthenticationService.SIGNIN_REDIRECT);
    }
  }

  /**
   * This command will both return and clear the saved SignInRedirect URL.
   * An additional call to {@link setSignInRedirect} is required to re-save
   * the URL after retrieving it (if desired), since that is likely to be uncommon.
   */
  public getSignInRedirect(): string {
    const url = this._authStorage.getItem(AuthenticationService.SIGNIN_REDIRECT);
    this._authStorage.removeItem(AuthenticationService.SIGNIN_REDIRECT);
    return url;
  }

  public async routeToSignInRedirect(): Promise<void> {
    const url = this.getSignInRedirect();
    if (url) {
      await this.router.navigate([url]);
    } else {
      await this.router.navigate([CommonConstants.appRoutes.overview.overviewBase]);
    }
  }

  public async getMfaPreference(): Promise<FetchMFAPreferenceOutput> {
    const cachedPreference = this._authStorage.getItem(AuthenticationService.MFA_PREFERENCE_CACHE);
    if (cachedPreference) {
      return JSON.parse(cachedPreference);
    }

    const mfaPreference = await fetchMFAPreference();
    mfaPreference.enabled ??= [];
    this._authStorage.setItem(AuthenticationService.MFA_PREFERENCE_CACHE, JSON.stringify(mfaPreference));

    return mfaPreference;
  }

  public isMfaRequired(): boolean {
    return this.getAuthStatus() === 'LIMITED_AUTH';
  }

  public async isMfaOfferedButNotRequired(): Promise<boolean> {
    const authPreferences = await this.getAuthPreferences();
    if (!authPreferences) {
      return false;
    }

    const mfaResult = await this.getMfaPreference();
    const enabledMfa = new Set(mfaResult.enabled.map((method) => method.toLowerCase()));
    const offeredMfa = authPreferences.offeredMfaMethods;

    return offeredMfa.some((method) => !enabledMfa.has(method.toLowerCase()));
  }

  public async isEmailMfaEnabled(): Promise<boolean> {
    const mfaResult = await this.getMfaPreference();

    return !!mfaResult.enabled?.some((method) => method.toLowerCase() === 'email');
  }

  public async isSmsMfaEnabled(): Promise<boolean> {
    const mfaResult = await this.getMfaPreference();

    return !!mfaResult.enabled?.some((method) => method.toLowerCase() === 'sms');
  }

  public async getPhoneNumber() {
    const userAttributes = await fetchUserAttributes();
    return {
      phone_number: userAttributes.phone_number,
      phone_number_verified: userAttributes.phone_number_verified,
    };
  }

  public async setPhoneNumber(phoneNumber: string) {
    const formattedPhoneNumber = PhoneNumbers.formatPhoneNumberForCognito(phoneNumber);

    try {
      const updateResult = await updateUserAttribute({
        userAttribute: {
          attributeKey: 'phone_number',
          value: formattedPhoneNumber,
        },
      });

      this._lastConfirmAttribute = '';
      this._lastDeliveryDestination = '';
      this._lastDeliveryMedium = '';

      if (updateResult.nextStep.updateAttributeStep === 'CONFIRM_ATTRIBUTE_WITH_CODE') {
        // this doesn't seem to be returning as we would expect...code flows through DONE block

        this._lastConfirmAttribute = updateResult.nextStep.codeDeliveryDetails?.attributeName ?? '';
        this._lastDeliveryDestination = updateResult.nextStep.codeDeliveryDetails?.destination ?? '';
        this._lastDeliveryMedium = updateResult.nextStep.codeDeliveryDetails?.deliveryMedium ?? '';
      } else if (updateResult.nextStep.updateAttributeStep === 'DONE') {
        // updating the phone number seems to always require an extra call to send the verification code

        await this.sendPhoneNumberVerificationCode();
      } else {
        return 'UNEXPECTED_SIGNUP_RESPONSE';
      }
    } catch (_error) {
      return 'UNKNOWN_ERROR';
    }

    return '';
  }

  public async sendPhoneNumberVerificationCode() {
    try {
      const verifyResult = await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });

      this._lastConfirmAttribute = verifyResult.attributeName ?? '';
      this._lastDeliveryDestination = verifyResult.destination ?? '';
      this._lastDeliveryMedium = verifyResult.deliveryMedium ?? '';
    } catch (error) {
      switch ((error as { name?: string })?.name ?? '') {
        case 'LimitExceededException':
          return 'LIMIT_EXCEEDED_ERROR';
      }

      return 'UNKNOWN_ERROR';
    }

    return '';
  }

  public async confirmPhoneNumber(confirmationCode: string) {
    try {
      await confirmUserAttribute({
        userAttributeKey: 'phone_number',
        confirmationCode,
      });
    } catch (error) {
      this.raygunService.logError('Confirm Phone Number error', { error });

      switch ((error as { name?: string })?.name ?? '') {
        case 'CodeMismatchException':
          return 'CODE_MISMATCH_ERROR';
      }

      return 'UNKNOWN_ERROR';
    }

    return '';
  }

  public async enableEmailMFA() {
    return this.updateMFAPreference({ email: 'ENABLED' });
  }

  public async disableEmailMFA() {
    return this.updateMFAPreference({ email: 'DISABLED' });
  }

  public async enableSMSMFA() {
    return this.updateMFAPreference({ sms: 'ENABLED' });
  }

  public async disableSMSMFA() {
    return this.updateMFAPreference({ sms: 'DISABLED' });
  }

  private async confirmAndEnableSMSMFA_sub() {
    const mfaError = await this.enableSMSMFA();

    if (mfaError) {
      return mfaError;
    }

    this._lastCreds = undefined;
    this._lastConfirmAttribute = '';
    this._lastDeliveryDestination = '';
    this._lastDeliveryMedium = '';

    return '';
  }

  public async confirmPhoneNumberAndEnableSMSMFA(confirmationCode: string) {
    const confirmError = await this.confirmPhoneNumber(confirmationCode);
    if (confirmError) {
      return confirmError;
    }

    return this.confirmAndEnableSMSMFA_sub();
  }

  public async changePassword(oldPassword: string, newPassword: string) {
    try {
      await updatePassword({
        oldPassword,
        newPassword,
      });

      return '';
    } catch (error) {
      switch ((error as { name?: string })?.name) {
        case 'NotAuthorizedException':
          // 'oldPassword' was incorrect
          return 'NOT_AUTHORIZED_ERROR';
        case 'InvalidParameterException':
          // 'newPassword' does not meet requirements
          return 'INVALID_PASSWORD_ERROR';
        case 'LimitExceededException':
          return 'LIMIT_EXCEEDED_ERROR';
        default:
          return 'UNKNOWN_ERROR';
      }
    }
  }

  /**
   * Changes the users phone number associated with their AWS account.
   * It is recommended that the phone number adhere to the E.164 format +9 (999) 999-9999
   * It is also recommended that phone numbers with extensions adhere to the RFC 3966 format +9 (999) 999-9999;ext=9999
   * @param phoneNumber The new phone number the user would like to use.
   * @see https://openid.net/specs/openid-connect-core-1_0.html#StandardClaims
   */
  public async changeCognitoPhoneNumber(phoneNumber: string) {
    const formattedPhoneNumber = PhoneNumbers.formatPhoneNumberForCognito(phoneNumber);

    try {
      await updateUserAttribute({
        userAttribute: {
          attributeKey: 'phone_number',
          value: formattedPhoneNumber,
        },
      });

      this.storePhoneNumber(formattedPhoneNumber);

      return '';
    } catch (error) {
      switch ((error as { name?: string })?.name) {
        case 'InvalidParameterException':
          return 'INVALID_PHONE_NUMBER_FORMAT';
        default:
          return 'UNKNOWN_ERROR';
      }
    }
  }

  public async getProfileInfo(): Promise<ProfileInfo> {
    const authSession = await fetchAuthSession().catch((_error) => {
      throw new AuthError({ code: 'SESSION_ERROR', message: 'Error Getting Session' });
    });

    if (!authSession.tokens?.idToken?.payload['custom:profile_id']) {
      throw new AuthError({ code: 'SESSION_ERROR', message: 'Profile Info Missing' });
    }

    const username = authSession.tokens?.idToken?.payload['cognito:username'] as string;
    const idToken = authSession.tokens?.idToken;
    const client_count = authSession.tokens?.idToken?.payload['custom:clients'] as number;
    const division_count = authSession.tokens?.idToken?.payload['custom:divisions'] as number;
    const client_id = authSession.tokens?.idToken?.payload['custom:client_id'] as string;

    return { username, idToken, client_count, division_count, client_id };
  }

  public async getAvailableForgotPasswordMethods(username: string): Promise<string[]> {
    const { response, error } = await firstValueFrom(this.securityService.getAvailableForgotPasswordMethods(username));

    if (error) {
      throw new AuthError({ code: error.error, message: error.message });
    }

    return response;
  }

  /**
   * Initiates a password reset request. Sends a confirmation code to the users email address or phone, depending on their MFA settings.
   * @param username The username (email address) of the user that forgot their password.
   */
  public async forgotPassword(username: string, method: 'email' | 'sms') {
    const { response, error } = await firstValueFrom(this.securityService.forgotPassword(username, method));

    if (error) {
      return error.message;
    }

    this.setEmailAddress(username);

    const deliveryDetails = response.codeDeliveryDetails;
    this._lastConfirmAttribute = deliveryDetails.attributeName;
    this._lastDeliveryMedium = deliveryDetails.deliveryMedium.toLowerCase();
    this._lastDeliveryDestination = deliveryDetails.destination;

    if (!['sms', 'email'].includes(this.lastDeliveryMedium)) {
      this.raygunService.logError('Unhandled forgotPassword() delivery medium', { response });
      return 'UNKNOWN_DELIVERY_MEDIUM';
    }

    if (this.lastDeliveryMedium === 'sms') {
      this.storePhoneNumber(this.lastDeliveryDestination);
      await this.router.navigate(['two-factor-authentication'], { queryParams: { type: 'phone' }, state: { titleText: 'Forgot Password' } });
    } else {
      await this.router.navigate(['two-factor-authentication'], { queryParams: { type: 'email' }, state: { titleText: 'Forgot Password' } });
    }
  }

  /**
   * Confirms that the user provided the correct forgot password verification code and resets their password to the desired password.
   * @param code Verification code entered by the user.
   * @param desiredPassword The new password the user would like to use.
   */
  public async confirmForgotPassword(code: string, desiredPassword: string) {
    const emailAddress = this.currentUserQuery.getEmailAddress();
    const { error } = await firstValueFrom(this.securityService.confirmForgotPassword(emailAddress, code, desiredPassword));

    if (error) {
      if (error.error === 'InvalidCodeException') {
        const twoFaType = this.lastDeliveryMedium === 'sms' ? 'phone' : 'email';
        await this.router.navigate(['two-factor-authentication'], {
          queryParams: {
            type: twoFaType,
          },
          state: {
            error: error.message,
          },
        });
      }

      return error.message;
    }

    this._lastCreds = undefined;
    this._lastConfirmAttribute = '';
    this._lastDeliveryDestination = '';
    this._lastDeliveryMedium = '';

    return '';
  }

  /**
   * Sends a verification code to the users email.
   */
  public async verifyEmailAddress() {
    return this.verifyUserAttribute('email');
  }

  /**
   * Sends a verification code to the users phone.
   */
  public async verifyCognitoPhoneNumber() {
    return this.verifyUserAttribute('phone_number');
  }

  public async verifyUserAttribute(attribute: VerifiableUserAttributeKey) {
    try {
      await sendUserAttributeVerificationCode({ userAttributeKey: attribute });

      if (attribute === 'phone_number') {
        this.currentUserStore.update({
          mfaType: MFAType.SMS,
        });
      }

      return '';
    } catch (error) {
      this.raygunService.logError('Verify user attribute error', { error });

      switch ((error as { name?: string })?.name) {
        case 'LimitExceededException':
          return 'LIMIT_EXCEEDED_ERROR';
        default:
          return 'UNKNOWN_ERROR';
      }
    }
  }

  public async confirmUserAttributeVerification(attribute: VerifiableUserAttributeKey, confirmationCode: string) {
    try {
      await confirmUserAttribute({
        userAttributeKey: attribute,
        confirmationCode,
      });

      return '';
    } catch (error) {
      this.raygunService.logError('Confirm user attribute error', { error });

      switch ((error as { name?: string })?.name) {
        case 'CodeMismatchException':
          return 'CODE_MISMATCH_ERROR';
        case 'LimitExceededException':
          return 'LIMIT_EXCEEDED_ERROR';
        case 'ExpiredCodeException':
          return 'EXPIRED_CODE_ERROR';
        default:
          return 'UNKNOWN_ERROR';
      }
    }
  }

  /**
   * Looks up a user to see if there is a configured SSO option that should be presented to them during sign in.
   * @param username The username to authenticate with. Should be an email address.
   */
  public async getAuthenticationChallengeType(username: string): Promise<AuthenticationChallenge> {
    this.browserSessionStorageService.set('email', username);
    try {
      const queryData: SearchCriteria = [
        {
          key: 'email',
          value: username,
          matchType: MatchType.EXACT,
        },
        {
          key: 'appType',
          value: 'CU',
          matchType: MatchType.EXACT,
        },
      ];
      const uri = new Uri('/profile/profileType/activeClientIdp/search', CoreService.Profile);
      const loginInfos: LoginInfo[] = await firstValueFrom(this.http.post<LoginInfo[]>(uri.toString(), queryData));
      if (loginInfos && loginInfos.length > 0) {
        if (loginInfos.length > 1) {
          const multipleIdentityProvidersError = new AuthError({
            code: 'IDENTITY_PROVIDER_ERROR',
            message: 'Multiple identity providers given. Defaulting to the first identity provider.',
          });
          this.raygunService.logError(multipleIdentityProvidersError, { loginInfos });
        }
        const loginInfo: LoginInfo = loginInfos[0];
        if (loginInfo.loginType === LoginType.SAML) {
          if (loginInfo.identityProvider) {
            return { challengeType: AuthenticationChallengeType.SAML, identityProvider: loginInfo.identityProvider, idpOverrides: loginInfo.idpOverrides };
          }
          const noIdentityProviderError = new AuthError({
            code: 'IDENTITY_PROVIDER_ERROR',
            message: 'System is configured for SAML authentication but no identity provider is specified.',
          });
          this.raygunService.logError(noIdentityProviderError, { loginInfo });
          return { challengeType: AuthenticationChallengeType.Unknown, error: noIdentityProviderError };
        }
      }
      return { challengeType: AuthenticationChallengeType.Password };
    } catch (error) {
      this.raygunService.logError('Authentication challenge type error', { error, username });
      return {
        error: error instanceof Error ? error : new Error(String(error)),
        challengeType: AuthenticationChallengeType.Failed,
      };
    }
  }

  public async hasValidSession(): Promise<boolean> {
    try {
      await fetchAuthSession();
      return true;
    } catch (_error) {
      return false;
    }
  }

  public async signInWithCustomProvider(providerName: string): Promise<void> {
    const signInPayload = {
      provider: {
        custom: providerName,
      },
    };

    try {
      await signInWithRedirect(signInPayload);
    } catch (error) {
      if ((error as { name?: string }).name === 'UserAlreadyAuthenticatedException') {
        try {
          await signOut();
          await signInWithRedirect(signInPayload);
        } catch (retryError) {
          this.raygunService.logError('Failed during retry signInWithCustomProvider', { retryError });
        }
      } else {
        this.raygunService.logError('Failed to initiate signInWithCustomProvider', { error });
      }
    }
  }

  public setupHubListener(): void {
    if (this.hubCallback) {
      return;
    }

    this.hubCallback = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          void this.handleSuccessfulCustomProviderSignIn();
          break;
        case 'signInWithRedirect_failure':
          this.browserSessionStorageService.set(SessionStorage.FederatedError, 'Something went wrong');
          void this.router.navigate(['/logout']);
          break;
      }
    });
  }

  public stopHubListener(): void {
    if (this.hubCallback) {
      this.hubCallback();
      this.hubCallback = undefined;
    }
  }

  public async handleSuccessfulCustomProviderSignIn(): Promise<boolean> {
    const pathname = this.browserService.getLocationPathname();
    const isIdPi = pathname === '/login/federatedIdPi';
    try {
      await this.getValidIdToken();

      const emailFromLogin: string = this.browserSessionStorageService.get('email');
      const currentUserEmail: string = this.currentUserQuery.getDecodedToken().email;

      if (!isIdPi && emailFromLogin && currentUserEmail && emailFromLogin.toLowerCase() !== currentUserEmail.toLowerCase()) {
        this.raygunService.logError('Single sign on email entered is not the same as the email returned from federated user', { currentUserEmail });
        this.browserSessionStorageService.set(SessionStorage.FederatedError, 'Please sign out and sign in again through your employer portal.');
        await this.router.navigate(['/logout']);
      }

      this.setEmailAddress(currentUserEmail);
      await this.directUserAfterSignIn();
    } catch (error) {
      this.raygunService.logError('Error handling successful sign-in', { error });
      return this.router.navigate(['/logout']);
    }
  }

  /**
   * Used in flows where the user is required to provide more context to disambiguate their login.
   * Examples are when a user is associated with more than one client or division and they are required to select one for their session.
   */
  public async signInWithStoredCredentials(extra?: { clientId?: string; divisionId?: string }, logErrorIfNoCredentials: boolean = false, options: SignInOptions = {}) {
    const error = await (this._lastCreds?.u && this._lastCreds?.p
      ? this.signInAndRedirect(this._lastCreds.u, this._lastCreds.p, this._rememberMe, extra)
      : this._lastCreds?.u && this._lastCreds?.duxToken
        ? this.duxSSOSignInAndRedirect(this._lastCreds.u, this._lastCreds.duxToken, extra)
        : Promise.resolve('NO_SAVED_CREDENTIALS' as const));

    if (error === 'NO_SAVED_CREDENTIALS') {
      if (logErrorIfNoCredentials) {
        this.raygunService.logError('Stored credentials not found');
      }
      return error;
    }

    if (!options.keepCredentials) {
      this._lastCreds = undefined;
    }

    return error;
  }

  public async updateAuthStatus() {
    try {
      const authSession = await fetchAuthSession();
      const idToken = authSession.tokens?.idToken;

      if (!idToken) {
        this.authStatus.next('LOGGED_OUT');
        this.currentUserStore.update({ idToken: null });
        return;
      }

      this.currentUserStore.update({ idToken });

      // If user has MFA idp_group, that means the pre-auth cognito trigger has determined they do not have enough MFA options enabled
      // We return 'LIMITED_SESSION' to recognize they have very limited site access until they set up mfa (can essentially only access a dashboard and terms of use endpoint)
      if (idToken.payload['custom:idp_group'] === 'MFA') {
        this.authStatus.next('LIMITED_AUTH');
        return;
      }

      this.authStatus.next('FULL_AUTH');
    } catch (_error) {
      return 'LOGGED_OUT';
    }
  }

  public getAuthStatus(): AuthStatus {
    const authStatus = this.authStatus.getValue();
    return authStatus;
  }

  public async isSSOUser() {
    const authSession = await fetchAuthSession();
    return !!authSession.tokens?.idToken?.payload?.['identities'];
  }

  private async setCurrentUser(data: ProfileInfo | { idToken?: JWT }): Promise<void> {
    const idToken = data.idToken || null;

    this.currentUserStore.update({ idToken });

    if (idToken) {
      const { phone_number } = await this.getPhoneNumber();
      this.storePhoneNumber(phone_number);
    }
  }

  private async rememberOrForgetDevice(): Promise<void> {
    try {
      if (this._rememberMe === RememberUserAction.Remember) {
        await rememberDevice();
        this.currentUserStore.rememberUser(this.currentUserQuery.getEmailAddress());
      } else if (this._rememberMe === RememberUserAction.Forget) {
        await this.deviceNotRemembered();
        this.currentUserStore.forgetUser();
      }
    } catch (error) {
      this.raygunService.logError('Remember or forget device failed', { error });
    }
  }

  private setEmailAddress(emailAddress: string): void {
    if (emailAddress) {
      this.raygunService.setUser(emailAddress, emailAddress);
      this.currentUserStore.update({
        email: emailAddress,
      });
    } else {
      this.currentUserStore.reset();
      this.raygunService.clearUser();
    }
  }

  private storePhoneNumber(phoneNumber: string): void {
    const formattedPhoneNumber = phoneNumber ? PhoneNumbers.formatPhoneNumberForUBA(phoneNumber) : null;
    this.currentUserStore.update({
      cognitoPhoneNumber: formattedPhoneNumber,
    });
  }

  private async updateMFAPreference(preference: UpdateMFAPreferenceInput) {
    try {
      await updateMFAPreference(preference);
      this._authStorage.removeItem(AuthenticationService.MFA_PREFERENCE_CACHE);
    } catch (_error) {
      return 'UNKNOWN_ERROR';
    }

    return '';
  }

  private async getAuthPreferences(): Promise<AuthPreferences | null> {
    const cachedPreference = this._authStorage.getItem(AuthenticationService.AUTH_PREFERENCE_CACHE);
    if (cachedPreference) {
      return JSON.parse(cachedPreference);
    }

    const clientId = this.currentUserQuery.getClientIdFromToken();
    if (!clientId) {
      return null;
    }

    const authPreferences = await firstValueFrom(this.dashboardService.getClientMfaRequirements(clientId));
    if (!authPreferences) {
      return null;
    }

    this._authStorage.setItem(AuthenticationService.AUTH_PREFERENCE_CACHE, JSON.stringify(authPreferences));

    return authPreferences;
  }

  public async deviceNotRemembered() {
    const { tokens } = await fetchAuthSession();

    if (!tokens?.accessToken) {
      return;
    }

    const tokenOrchestrator = cognitoUserPoolsTokenProvider.tokenOrchestrator;
    const deviceMetadata = await tokenOrchestrator?.getDeviceMetadata();

    if (!deviceMetadata?.deviceKey) {
      return;
    }

    const body = {
      AccessToken: tokens.accessToken.toString(),
      DeviceKey: deviceMetadata.deviceKey,
      DeviceRememberedStatus: 'not_remembered',
    };

    const url = new URL('https://cognito-idp.us-east-1.amazonaws.com/');
    const method = 'POST';
    const headers = {
      'content-type': 'application/x-amz-json-1.1',
      'x-amz-target': 'AWSCognitoIdentityProviderService.UpdateDeviceStatus',
      'cache-control': 'no-store',
      'x-amz-user-agent': 'aws-amplify/6.15.0 auth/32 framework/100',
      'amz-sdk-invocation-id': v4(),
      'amz-sdk-request': 'attempt=1; max=3',
    };
    const cache = 'no-store';
    const withCrossDomainCredentials = false;

    const resp = await fetch(url, {
      method,
      headers,
      body: JSON.stringify(body),
      cache,
      credentials: withCrossDomainCredentials ? 'include' : 'same-origin',
    });

    if (resp.status !== 200) {
      throw new Error('x-amzn-errormessage');
    }
  }
}
